import { Component, HostListener } from '@angular/core';
import {
    Router,
    Event as RouterEvent,
    NavigationStart,
    NavigationEnd,
    NavigationError,
    NavigationCancel,
    ResolveStart,
    ResolveEnd,
} from '@angular/router';
import { Platform, MenuController, } from '@ionic/angular';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';

import { AnalyticsService } from "./services/analytics.service";
import { EventsService } from "./services/events.service";
import { AuthService } from "./auth/auth.service";
import { ErrorHandlerService } from "./services/error-handler.service";

import { AnalyticsNames } from "./constants";

import { environment } from '../environments/environment';

import {
    MOBILE_RESIZE_BOTTOM_BAR_DIFF,
    MOBILE_RESIZE_ADDRESS_BAR,
    MOBILE_RESIZE_TIMEOUT,
    MOBILE_RESIZE_SCROLL_BUFFER,
    MOBILE_RESIZE_ADDRESS_BAR_BOTTOM_BAR,
    UNLOAD_PROMPT_TEXT,
} from "./constants";

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent {
    public loading: boolean = false;
    public username: string = "";
    public resourcesShown: boolean = false;

    constructor(
        private platform: Platform,
        private splashScreen: SplashScreen,
        private statusBar: StatusBar,
        private router: Router,
        private menuCtrl: MenuController,
        private events: EventsService,
        private auth: AuthService,
        private errorServ: ErrorHandlerService,
        private analytics: AnalyticsService,
    ) {
        this.initializeApp();
    }

    @HostListener("window:beforeunload", ['$event'])
    public onBeforeUnload($event): boolean {
        if ((environment as any).production) {
            this.analytics.closeout();
            if (/^\/map\/(?:create|[0-9]+)/.test(this.router.url)) {
                $event.preventDefault();
                $event.returnValue = UNLOAD_PROMPT_TEXT;
                return window.confirm(UNLOAD_PROMPT_TEXT);
            }
        }
        return true;
    }

    public go(page: string): void {
        this.menuCtrl.close("mappage");
        this.events.publish("mappage:open-page", page);
    }

    public async signout(): Promise<any> {
        this.menuCtrl.close("mappage");
        try {
            const cont = window.confirm(UNLOAD_PROMPT_TEXT);
            this.analytics.track(AnalyticsNames.APP, "signout-menu-clicked", { canceled: !cont });
            if (cont) {
                await this.auth.signout();
                this.router.navigate(["/user/login"]);
            }
        } catch (e) {
            this.errorServ.logError(e);
            console.error("Error signing out", e);
        }
    }

    public showResources(): void {
        this.resourcesShown = true;
    }

    public hideResources(): void {
        this.resourcesShown = false;
    }

    private initializeApp(): void {
        this.router.events.subscribe((event: RouterEvent) => {
            this.checkRouterEvent(event);

            if (event instanceof NavigationStart) { // && !event.url.startsWith("/login")
                this.analytics.track(AnalyticsNames.APP + ":nav-start", event.url);
            }
        });

        this.auth.getAuthState().subscribe(authState => {
            this.username = authState && authState.user ? authState.user.username : "";
            console.info("username", this.username);
        });

        this.platform.ready().then(() => {
            this.statusBar.styleDefault();
            this.splashScreen.hide();

            if (this.isMobile()) {
                // TODO: debounce
                window.addEventListener("resize", this.mobileSizingCheck.bind(this));
                document.body.addEventListener("focusin", ev => {
                    const t = (ev.target as HTMLInputElement);
                    if (t.tagName === "INPUT") {
                        this.onInputFocus(t);
                    }
                });
                this.mobileSizingCheck();
            }
        });
    }

    private async checkRouterEvent(event: RouterEvent): Promise<any> {
        if (event instanceof ResolveStart) {
            this.loading = true;
        } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
            this.loading = false;
            if (this.isMobile()) {
                this.mobileSizingCheck();
            }
        }
    }

    private isMobile(): boolean {
        const userAgent = window.navigator.userAgent;
        return this.isIos(userAgent);
    }

    private isIos(userAgent: string): boolean {
        // TODO: use library
        return /ip(?:hone|ad|od)/i.test(userAgent) && /safari/i.test(userAgent);
    }

    private mobileSizingCheck(): void {
        let h: string;
        let d: string;
        let m: string;
        if (this.hasAddressBar()) {
            h = window.innerHeight + "px"; //`calc(100% - ${MOBILE_RESIZE_ADDRESS_BAR}px) !important`;
            m = window.outerHeight - window.innerHeight + "px";

            if (this.hasBottomBar()) {
                //h = `calc(100% - ${MOBILE_RESIZE_ADDRESS_BAR_BOTTOM_BAR}px) !important`;

                // TODO: set indicators to block?
                // const els = document.querySelectorAll("indicators");
                // els.forEach(el => el.style.display = "block !important");
                d = "block !important";
            }
            setTimeout(() => document.body.scrollTop = (window.outerHeight - window.innerHeight), MOBILE_RESIZE_TIMEOUT);
        } else {
            h = window.outerHeight + "px";
            m = "0px";
            setTimeout(() => document.body.scrollTop = 0, MOBILE_RESIZE_TIMEOUT);
        }
        document.body.style.height = h;
        document.body.style.marginTop = m;
        document.querySelectorAll("indicators").forEach((el: any) => el.style.display = d);
    }

    private onInputFocus(target: HTMLInputElement): void {
        setTimeout(() => {
            if (!this.hasAddressBar()) {
                document.body.scrollTop = document.documentElement.scrollHeight;
            } else {
                const footers = document.getElementsByTagName("ion-footer");
                if (footers.length > 0) {
                    document.body.scrollTop = document.documentElement.scrollHeight - (2 * footers[0].getBoundingClientRect().height);
                } else {
                    const scrollTop = window.pageYOffset || target.scrollTop;
                    const finalOffset = target.getBoundingClientRect().top + scrollTop;
                    document.body.scrollTop = finalOffset - MOBILE_RESIZE_SCROLL_BUFFER;
                }
            }
        }, MOBILE_RESIZE_TIMEOUT);
    }

    private hasAddressBar(): boolean {
        return (this.sizeDif() < MOBILE_RESIZE_ADDRESS_BAR);
    }

    private hasBottomBar(): boolean {
        return (this.sizeDif() < -MOBILE_RESIZE_BOTTOM_BAR_DIFF);
    }

    private sizeDif(): number {
        return (window.innerHeight - document.documentElement.clientHeight);
    }
}
